import { useState } from 'react';
import { type AnnouncementRecipientDto } from '@/app/models/AnnouncementRecipientDto';
import AnnouncementModal from '@/app/modules/announcements/components/AnnouncementModal';
import { usePendingAnnouncements } from '@/app/modules/announcements/core/_hooks';
import { isMandatory } from '@/app/modules/announcements/core/_utils';
import { ContactType } from '@/app/models/Contact';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { getUserTypeFromJWT } from '@/app/modules/auth/core/_helpers';
import WelcomeModal from '@/app/pages/home/components/WelcomeModal';

// As boas-vindas são da home do lojista; contador e gestor têm painel próprio e nunca as viram.
const WELCOME_EXCLUDED_USER_TYPES: Set<ContactType> = new Set([ContactType.ACCOUNTANT, ContactType.COMMUNITY]);

// Um modal por vez na entrada: obrigatório, boas-vindas, dispensável.
const EntryModals = () => {
  const { auth, currentUser } = useAuth();
  const { data: pending = [], isPending } = usePendingAnnouncements();
  // Some já ao fechar, sem esperar a lista de pendentes voltar do backend.
  const [closedIds, setClosedIds] = useState<Set<string>>(new Set());
  // Mantém o comunicado aberto até a ação terminar, mesmo se um refetch o tirar dos pendentes.
  const [shown, setShown] = useState<AnnouncementRecipientDto>();
  const open = pending.filter((announcement) => !closedIds.has(announcement.id));
  const userType = auth ? getUserTypeFromJWT(auth.token) : undefined;
  const welcomeExcluded = userType !== undefined && WELCOME_EXCLUDED_USER_TYPES.has(userType);
  const welcome = currentUser?.first_access === true && !welcomeExcluded;

  const closeAnnouncement = (id: string) => {
    setClosedIds((previous) => new Set(previous).add(id));
    setShown(undefined);
  };

  if (shown) {
    return <AnnouncementModal key={shown.id} announcement={shown} onClose={() => closeAnnouncement(shown.id)} />;
  }

  // Enquanto carrega, `pending` é [] por padrão; sem essa espera as boas-vindas aparecem antes do obrigatório.
  if (isPending) return null;

  const mandatory = open.find((announcement) => isMandatory(announcement.type.value));
  if (mandatory) {
    // Atualizar o estado no render faz o React refazer o render na hora, já com o modal.
    setShown(mandatory);
    return null;
  }

  if (welcome) return <WelcomeModal />;

  const dismissible = open.find((announcement) => !isMandatory(announcement.type.value));
  if (dismissible) setShown(dismissible);
  return null;
};

export default EntryModals;
